import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus, X, ShoppingCart, BookOpen, Sparkles, Loader2,
  ChevronUp, ChevronDown, Star, Clock, Users, AlertTriangle,
  StickyNote, ChevronLeft, ChevronRight, Trash2, Check, UserPlus,
} from 'lucide-react';
import { TabBar, TabItem } from '@/components/ui/TabBar';
import { EmptyState } from '@/components/ui/EmptyState';
import { getDocs, addDoc, updateDoc, collection, serverTimestamp, doc } from 'firebase/firestore';
import { db } from '@/firebase';
import { AppState, Recipe, RecipeIngredient, RecipeStep, GuestType, ExternalGuest, PlannedMealItem } from '@/types';
import { useMealPlan, PlannedMeal, MealSlot, GrocerySyncResult } from '../hooks/useMealPlan';
import { useAiAction } from '@/hooks/useAiAction';

// ─── Constants ───────────────────────────────────────────────────────────────

const RECIPE_TAGS = ['Vegan', 'Vegetarian', 'Gluten-Free', 'Nut-Free', 'Dairy-Free', 'Quick', 'Kid-Friendly'];

const DIETARY_TAG_MAP: Record<string, string[]> = {
  vegan:       ['Vegan'],
  vegetarian:  ['Vegan', 'Vegetarian'],
  'gluten-free': ['Gluten-Free'],
  'nut-free':  ['Nut-Free'],
  'dairy-free': ['Dairy-Free'],
};

const UNIT_OPTIONS = ['cup', 'tbsp', 'tsp', 'oz', 'g', 'lb', 'ml', 'l', 'count', 'each', 'slice', 'clove', 'pinch'];

const GUEST_COLORS: Record<GuestType, { border: string; badge: string; dot: string }> = {
  family:     { border: 'border-amber-400/60',  badge: 'bg-amber-400/20 text-amber-300',  dot: 'bg-amber-400' },
  friends:    { border: 'border-violet-400/60', badge: 'bg-violet-400/20 text-violet-300', dot: 'bg-violet-400' },
  colleagues: { border: 'border-sky-400/60',    badge: 'bg-sky-400/20 text-sky-300',       dot: 'bg-sky-400' },
  other:      { border: 'border-slate-400/60',  badge: 'bg-slate-400/20 text-slate-300',   dot: 'bg-slate-400' },
};

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const SLOTS: MealSlot[] = ['breakfast', 'lunch', 'dinner', 'snack'];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getMondayOf = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - day + (day === 0 ? -6 : 1));
  d.setHours(0, 0, 0, 0);
  return d;
};

const getWeekDays = (monday: Date): Date[] =>
  Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(d.getDate() + i);
    return d;
  });

const fmtDate = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const fmtTime = (h: number, m: number) => `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

const calcPrepStartTime = (cookMins: number, prepMins: number, targetHour = 19): string => {
  const totalMins = (cookMins || 0) + (prepMins || 0);
  const startTotalMins = targetHour * 60 - totalMins;
  const h = Math.floor(startTotalMins / 60);
  const m = startTotalMins % 60;
  return fmtTime(Math.max(0, h), Math.max(0, m));
};

const getMealGuestType = (meal: PlannedMeal): GuestType | null => {
  if (!meal.externalGuests?.length) return null;
  return meal.externalGuests[0].type;
};

const getMealDisplayName = (meal: PlannedMeal): string => {
  if (meal.items?.length) return meal.items.map(i => i.recipeName).join(' + ');
  return meal.name || '';
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const DietaryConflictBadge: React.FC<{ recipe: Recipe; attendeeDietary: string[] }> = ({ recipe, attendeeDietary }) => {
  if (!attendeeDietary.length || !recipe.tags?.length) return null;
  const conflicts: string[] = [];
  attendeeDietary.forEach(restriction => {
    const requiredTags = DIETARY_TAG_MAP[restriction] || [];
    const hasConflict = requiredTags.length > 0 && !requiredTags.some(t => recipe.tags?.includes(t));
    if (hasConflict) conflicts.push(restriction);
  });
  if (!conflicts.length) return null;
  return (
    <div className="flex items-center gap-1 px-2 py-1 bg-amber-500/10 border border-amber-500/30 rounded-lg">
      <AlertTriangle className="w-3 h-3 text-amber-400 shrink-0" />
      <span className="text-[9px] font-black text-amber-400 uppercase tracking-wider">
        Dietary conflict: {conflicts.join(', ')}
      </span>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

interface MealPlannerProps {
  state: AppState;
  dispatch: React.Dispatch<any>;
}

export const MealPlanner: React.FC<MealPlannerProps> = ({ state }) => {
  const { t } = useTranslation();
  const householdId = state.currentHouseholdId;
  const currentUserId = state.currentUser?.id || '';
  const householdMembers = state.households.find(h => h.id === householdId)?.members ?? [];

  const [activeView, setActiveView] = useState<'planner' | 'recipes'>('planner');
  const [currentMonday, setCurrentMonday] = useState<Date>(() => getMondayOf(new Date()));
  const weekOf = currentMonday.toISOString().slice(0, 10);
  const weekDays = getWeekDays(currentMonday);

  const { plan, loading, saveMeal, deleteMeal, generateShoppingList, commitShoppingSync } = useMealPlan(householdId, weekOf);

  // ── Recipe state ──────────────────────────────────────────────────────────
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [recipesLoading, setRecipesLoading] = useState(false);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [recipeSearch, setRecipeSearch] = useState('');
  const [showAddRecipe, setShowAddRecipe] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState<Recipe | null>(null);

  // Recipe form state
  const [rfName, setRfName] = useState('');
  const [rfServings, setRfServings] = useState('2');
  const [rfPrep, setRfPrep] = useState('');
  const [rfCook, setRfCook] = useState('');
  const [rfIngredients, setRfIngredients] = useState<RecipeIngredient[]>([{ amount: '', unit: 'cup', item: '' }]);
  const [rfSteps, setRfSteps] = useState<RecipeStep[]>([{ step: 1, text: '' }]);
  const [rfTags, setRfTags] = useState<string[]>([]);
  const [rfSourceUrl, setRfSourceUrl] = useState('');
  const [rfStarred, setRfStarred] = useState(false);
  const [rfLeftoverDays, setRfLeftoverDays] = useState('0');
  const [isSavingRecipe, setIsSavingRecipe] = useState(false);

  // ── Planner state ─────────────────────────────────────────────────────────
  const [editingSlot, setEditingSlot] = useState<{ day: string; slot: MealSlot } | null>(null);
  const [editMeal, setEditMeal] = useState<PlannedMeal | null>(null);
  const [isAddingToPlan, setIsAddingToPlan] = useState(false);

  // Add-to-plan from recipe view
  const [addToPlanRecipe, setAddToPlanRecipe] = useState<Recipe | null>(null);
  const [addToPlanDay, setAddToPlanDay] = useState('');
  const [addToPlanSlot, setAddToPlanSlot] = useState<MealSlot>('dinner');

  // Grocery sync
  const [syncPreview, setSyncPreview] = useState<GrocerySyncResult | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');

  // AI
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [aiMealPlan, setAiMealPlan] = useState<Array<{ date: string; breakfast: string; lunch: string; dinner: string }>>([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [mealDietPrefs, setMealDietPrefs] = useState<string[]>([]);
  const { callAction: callAiAction } = useAiAction(householdId);
  const [pantryRecipes, setPantryRecipes] = useState<Array<{ name: string; missingIngredients: string[] }> | null>(null);
  const [pantryLoading, setPantryLoading] = useState(false);
  const [nutritionResult, setNutritionResult] = useState<Record<string, { calories: number; protein: string; carbs: string; fat: string }>>({});
  const [nutritionLoading, setNutritionLoading] = useState<string | null>(null);

  // ── Load recipes ──────────────────────────────────────────────────────────
  const loadRecipes = useCallback(async () => {
    if (!householdId) return;
    setRecipesLoading(true);
    try {
      const snap = await getDocs(collection(db, 'households', householdId, 'recipes'));
      setRecipes(snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })));
    } finally {
      setRecipesLoading(false);
    }
  }, [householdId]);

  useEffect(() => {
    if (activeView === 'recipes') loadRecipes();
  }, [activeView, loadRecipes]);

  // Also load recipes on mount for planner (needed for grocery sync)
  useEffect(() => {
    if (householdId) loadRecipes();
  }, [householdId, loadRecipes]);

  // ── Recipe form helpers ───────────────────────────────────────────────────
  const resetRecipeForm = () => {
    setRfName(''); setRfServings('2'); setRfPrep(''); setRfCook('');
    setRfIngredients([{ amount: '', unit: 'cup', item: '' }]);
    setRfSteps([{ step: 1, text: '' }]);
    setRfTags([]); setRfSourceUrl(''); setRfStarred(false); setRfLeftoverDays('0');
    setEditingRecipe(null);
  };

  const openAddRecipe = () => { resetRecipeForm(); setShowAddRecipe(true); };

  const openEditRecipe = (r: Recipe) => {
    setEditingRecipe(r);
    setRfName(r.name);
    setRfServings(String(r.servings || 2));
    setRfPrep(String(r.prepTimeMins || ''));
    setRfCook(String(r.cookTimeMins || ''));
    setRfIngredients(r.ingredients?.length ? r.ingredients : [{ amount: '', unit: 'cup', item: '' }]);
    setRfSteps(r.steps?.length ? r.steps : (r.instructions ? [{ step: 1, text: r.instructions }] : [{ step: 1, text: '' }]));
    setRfTags(r.tags || []);
    setRfSourceUrl(r.sourceUrl || '');
    setRfStarred(!!(r.isStarred || r.isFavorite));
    setRfLeftoverDays(String(r.leftoverDays || 0));
    setShowAddRecipe(true);
  };

  const addIngredientRow = () => setRfIngredients(p => [...p, { amount: '', unit: 'cup', item: '' }]);
  const removeIngredientRow = (i: number) => setRfIngredients(p => p.filter((_, idx) => idx !== i));
  const updateIngredient = (i: number, field: keyof RecipeIngredient, value: string) =>
    setRfIngredients(p => p.map((ing, idx) => idx === i ? { ...ing, [field]: value } : ing));

  const addStepRow = () => setRfSteps(p => [...p, { step: p.length + 1, text: '' }]);
  const removeStepRow = (i: number) => setRfSteps(p => p.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, step: idx + 1 })));
  const updateStep = (i: number, text: string) => setRfSteps(p => p.map((s, idx) => idx === i ? { ...s, text } : s));
  const moveStep = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= rfSteps.length) return;
    const next = [...rfSteps];
    [next[i], next[j]] = [next[j], next[i]];
    setRfSteps(next.map((s, idx) => ({ ...s, step: idx + 1 })));
  };

  const handleSaveRecipe = async () => {
    if (!rfName.trim() || !householdId || !state.currentUser) return;
    setIsSavingRecipe(true);
    try {
      const payload: any = {
        name: rfName.trim(),
        servings: parseInt(rfServings) || 2,
        prepTimeMins: rfPrep ? parseInt(rfPrep) : null,
        cookTimeMins: rfCook ? parseInt(rfCook) : null,
        ingredients: rfIngredients.filter(i => i.item.trim()),
        steps: rfSteps.filter(s => s.text.trim()),
        tags: rfTags,
        sourceUrl: rfSourceUrl || null,
        isStarred: rfStarred,
        isFavorite: rfStarred,
        leftoverDays: parseInt(rfLeftoverDays) || 0,
        householdId,
      };
      if (editingRecipe) {
        await updateDoc(doc(db, 'households', householdId, 'recipes', editingRecipe.id), payload);
      } else {
        payload.addedBy = state.currentUser.id;
        payload.createdAt = serverTimestamp();
        await addDoc(collection(db, 'households', householdId, 'recipes'), payload);
      }
      await loadRecipes();
      setShowAddRecipe(false);
      resetRecipeForm();
    } finally {
      setIsSavingRecipe(false);
    }
  };

  const handleToggleStar = async (recipe: Recipe) => {
    if (!householdId) return;
    const newVal = !recipe.isStarred;
    await updateDoc(doc(db, 'households', householdId, 'recipes', recipe.id), { isStarred: newVal, isFavorite: newVal });
    setRecipes(prev => prev.map(r => r.id === recipe.id ? { ...r, isStarred: newVal, isFavorite: newVal } : r));
  };

  // ── Planner helpers ───────────────────────────────────────────────────────
  const openSlot = (day: string, slot: MealSlot) => {
    const existing = plan?.days?.[day]?.[slot] ?? null;
    setEditingSlot({ day, slot });
    setEditMeal(existing ? { ...existing } : { items: [], plannedServings: 2, guestIds: [], externalGuests: [], rsvps: {}, notes: '' });
  };

  const handleSaveSlot = async () => {
    if (!editingSlot || !editMeal) return;
    setIsAddingToPlan(true);
    try {
      await saveMeal(editingSlot.day, editingSlot.slot, editMeal);
      setEditingSlot(null);
      setEditMeal(null);
    } finally {
      setIsAddingToPlan(false);
    }
  };

  const handleDeleteSlot = async () => {
    if (!editingSlot) return;
    await deleteMeal(editingSlot.day, editingSlot.slot);
    setEditingSlot(null);
    setEditMeal(null);
  };

  const addItemToMeal = (recipe: Recipe) => {
    if (!editMeal) return;
    const alreadyAdded = editMeal.items?.some(i => i.recipeId === recipe.id);
    if (alreadyAdded) return;
    setEditMeal(prev => ({
      ...prev!,
      items: [...(prev!.items || []), { recipeId: recipe.id, recipeName: recipe.name }],
    }));
  };

  const removeItemFromMeal = (idx: number) =>
    setEditMeal(prev => ({ ...prev!, items: (prev!.items || []).filter((_, i) => i !== idx) }));

  const toggleMealRsvp = (userId: string) => {
    setEditMeal(prev => {
      if (!prev) return prev;
      const current = prev.rsvps?.[userId] ?? null;
      const next = current === null ? 'confirmed' : current === 'confirmed' ? 'abstained' : null;
      return { ...prev, rsvps: { ...prev.rsvps, [userId]: next } };
    });
  };

  const toggleMealGuest = (userId: string) => {
    setEditMeal(prev => {
      if (!prev) return prev;
      const ids = prev.guestIds || [];
      const updated = ids.includes(userId) ? ids.filter(id => id !== userId) : [...ids, userId];
      const confirmed = updated.length + (prev.externalGuests?.length || 0);
      return { ...prev, guestIds: updated, plannedServings: Math.max(confirmed, 1) };
    });
  };

  const addExternalGuest = () => {
    setEditMeal(prev => ({
      ...prev!,
      externalGuests: [...(prev!.externalGuests || []), { name: '', type: 'family' as GuestType }],
    }));
  };

  const updateExternalGuest = (idx: number, field: keyof ExternalGuest, value: string) => {
    setEditMeal(prev => {
      if (!prev) return prev;
      const guests = [...(prev.externalGuests || [])];
      guests[idx] = { ...guests[idx], [field]: value };
      return { ...prev, externalGuests: guests };
    });
  };

  const removeExternalGuest = (idx: number) => {
    setEditMeal(prev => ({
      ...prev!,
      externalGuests: (prev!.externalGuests || []).filter((_, i) => i !== idx),
    }));
  };

  // ── Grocery sync ──────────────────────────────────────────────────────────
  const handlePreviewSync = async () => {
    if (!plan?.days) return;
    setIsSyncing(true);
    try {
      const result = await generateShoppingList(recipes);
      setSyncPreview(result);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleCommitSync = async () => {
    setIsSyncing(true);
    try {
      const { added, removed } = await commitShoppingSync(recipes);
      setSyncPreview(null);
      setSyncMessage(`Synced: +${added} added, ${removed} removed`);
      setTimeout(() => setSyncMessage(''), 4000);
    } finally {
      setIsSyncing(false);
    }
  };

  // ── AI helpers ────────────────────────────────────────────────────────────
  const handleSuggestFromPantry = async () => {
    setPantryLoading(true); setPantryRecipes(null);
    const pantryItems = (state as any).inventory?.filter((i: any) => (i.consumption?.remainingQuantity ?? 1) > 0).map((i: any) => i.title) ?? [];
    const result = await callAiAction<{ suggestions: Array<{ name: string; missingIngredients: string[] }> }>('suggestRecipesFromPantry', { pantryItems });
    if (result?.suggestions) setPantryRecipes(result.suggestions);
    setPantryLoading(false);
  };

  const handleAnalyzeNutrition = async (recipe: Recipe) => {
    setNutritionLoading(recipe.id);
    const ingList = recipe.ingredients?.map(i => `${i.amount} ${i.unit} ${i.item}`) ?? [];
    const result = await callAiAction<{ calories: number; protein: string; carbs: string; fat: string }>('analyzeNutrition', { recipeName: recipe.name, ingredients: ingList, servings: recipe.servings });
    if (result) setNutritionResult(prev => ({ ...prev, [recipe.id]: result }));
    setNutritionLoading(null);
  };

  const handleGenerateMealPlan = async () => {
    setAiLoading(true); setAiError(null); setAiMealPlan([]);
    const householdSize = state.households.find(h => h.id === householdId)?.memberIds?.length ?? 2;
    const result = await callAiAction<{ days: Array<{ date: string; breakfast: string; lunch: string; dinner: string }> }>('generateMealPlan', { householdSize, dietaryPreferences: mealDietPrefs, weekStartDate: weekOf });
    if (result?.days?.length) setAiMealPlan(result.days);
    else setAiError('Could not generate plan. Try again.');
    setAiLoading(false);
  };

  // ── Derived data ──────────────────────────────────────────────────────────
  const filteredRecipes = recipes.filter(r => {
    const matchTag = !selectedTag || r.tags?.includes(selectedTag);
    const matchSearch = !recipeSearch || r.name.toLowerCase().includes(recipeSearch.toLowerCase());
    return matchTag && matchSearch;
  }).sort((a, b) => ((b.isStarred || b.isFavorite) ? 1 : 0) - ((a.isStarred || a.isFavorite) ? 1 : 0));

  // Dietary restrictions of all confirmed meal attendees (for conflict detection)
  const getAttendeeDietary = (meal: PlannedMeal): string[] => {
    const attendeeIds = [...(meal.guestIds || [])];
    return (state as any).userProfiles?.filter((p: any) => attendeeIds.includes(p.id))
      .flatMap((p: any) => p.dietaryRestrictions || []) ?? [];
  };

  const slotLabel = (s: MealSlot) => s.charAt(0).toUpperCase() + s.slice(1);

  // ── Loading screen ─────────────────────────────────────────────────────────
  if (loading && activeView === 'planner') {
    return (
      <div className="p-6 animate-pulse space-y-4">
        <div className="h-8 bg-slate-800 rounded w-48" />
        <div className="grid grid-cols-7 gap-3">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="bg-slate-900 rounded-lg p-4 space-y-3">
              <div className="h-5 bg-slate-800 rounded w-12" />
              {Array.from({ length: 3 }).map((_, j) => <div key={j} className="h-20 bg-slate-800 rounded" />)}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <TabBar
        tabs={([
          { id: 'planner', label: t('meal.tab_planner', 'Meal Planner'), icon: <ShoppingCart className="w-3.5 h-3.5" /> },
          { id: 'recipes', label: t('meal.tab_recipes', 'Recipes'),      icon: <BookOpen className="w-3.5 h-3.5" /> },
        ] as TabItem[])}
        activeTab={activeView}
        onChange={id => setActiveView(id as 'planner' | 'recipes')}
        className="mb-6 w-fit"
      />

      {/* ══════════════════ RECIPES VIEW ══════════════════ */}
      {activeView === 'recipes' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white">{t('meal.recipe_library', 'Recipe Library')}</h2>
            <div className="flex items-center gap-2">
              <button onClick={handleSuggestFromPantry} disabled={pantryLoading}
                className="flex items-center gap-1.5 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white text-2xs font-black uppercase tracking-widest rounded-xl disabled:opacity-60 transition-colors">
                {pantryLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {t('meal.from_pantry', 'From Pantry')}
              </button>
              <button onClick={openAddRecipe}
                className="flex items-center gap-2 px-4 py-2 bg-brand text-white text-xs font-black uppercase tracking-widest rounded-full hover:bg-brand-hover transition-colors">
                <Plus className="w-3.5 h-3.5" /> {t('meal.add_recipe', 'Add Recipe')}
              </button>
            </div>
          </div>

          {pantryRecipes && pantryRecipes.length > 0 && (
            <div className="bg-amber-900/20 border border-amber-700/30 rounded-2xl p-4 space-y-2">
              <p className="text-2xs font-black uppercase tracking-wider text-amber-400">AI Pantry Suggestions</p>
              {pantryRecipes.map((r, i) => (
                <div key={i} className="flex items-center justify-between bg-slate-800 rounded-xl px-3 py-2">
                  <span className="text-sm font-bold text-white">{r.name}</span>
                  {r.missingIngredients.length > 0 && <span className="text-2xs text-amber-400">Missing: {r.missingIngredients.slice(0, 2).join(', ')}</span>}
                </div>
              ))}
            </div>
          )}

          <input type="text" value={recipeSearch} onChange={e => setRecipeSearch(e.target.value)}
            placeholder={t('meal.search_placeholder', 'Search recipes…')}
            className="w-full px-4 py-2.5 rounded-xl bg-slate-800 border border-slate-700 text-sm text-white focus:outline-none focus:border-blue-500 placeholder-slate-500" />

          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setSelectedTag(null)} className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${!selectedTag ? 'bg-brand text-white' : 'bg-slate-800 text-slate-400 hover:text-white'}`}>All</button>
            {RECIPE_TAGS.map(tag => (
              <button key={tag} onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${selectedTag === tag ? 'bg-brand text-white' : 'bg-slate-800 text-slate-400 hover:text-white'}`}>
                {tag}
              </button>
            ))}
          </div>

          {recipesLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1,2,3].map(i => <div key={i} className="h-48 bg-slate-800 rounded-2xl animate-pulse" />)}
            </div>
          ) : filteredRecipes.length === 0 ? (
            <EmptyState icon={<BookOpen className="w-8 h-8 text-slate-500" />} title={t('meal.no_recipes', 'No recipes yet — add your first recipe')} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredRecipes.map(recipe => (
                <div key={recipe.id} className="bg-slate-900 rounded-2xl p-5 border border-slate-800 space-y-3 hover:border-blue-700 transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-white font-bold text-sm leading-tight flex-1">{recipe.name}</h3>
                    <button onClick={() => handleToggleStar(recipe)} className="shrink-0 mt-0.5">
                      <Star className={`w-4 h-4 ${(recipe.isStarred || recipe.isFavorite) ? 'text-amber-400 fill-amber-400' : 'text-slate-600 hover:text-amber-400'} transition-colors`} />
                    </button>
                  </div>

                  <div className="flex items-center gap-3 text-xs text-slate-400">
                    <span>{recipe.servings} servings</span>
                    {(recipe.prepTimeMins || recipe.cookTimeMins) && (
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{(recipe.prepTimeMins || 0) + (recipe.cookTimeMins || 0)} min</span>
                    )}
                    {(recipe.leftoverDays ?? 0) > 0 && (
                      <span className="text-emerald-400">+{recipe.leftoverDays}d leftovers</span>
                    )}
                  </div>

                  {recipe.tags && recipe.tags.length > 0 && (
                    <div className="flex gap-1.5 flex-wrap">
                      {recipe.tags.map(tag => <span key={tag} className="px-2 py-0.5 bg-blue-900/30 text-blue-300 text-2xs font-bold rounded-full">{tag}</span>)}
                    </div>
                  )}

                  {recipe.ingredients?.length > 0 && (
                    <div className="text-2xs text-slate-500 space-y-0.5">
                      {recipe.ingredients.slice(0, 3).map((ing, i) => (
                        <p key={i}>{ing.amount} {ing.unit} {ing.item}</p>
                      ))}
                      {recipe.ingredients.length > 3 && <p className="text-slate-600">+{recipe.ingredients.length - 3} more</p>}
                    </div>
                  )}

                  {recipe.steps && recipe.steps.length > 0 && (
                    <p className="text-2xs text-slate-600 italic line-clamp-2">{recipe.steps[0]?.text}</p>
                  )}

                  {recipe.sourceUrl && (
                    <a href={recipe.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:underline block">View source →</a>
                  )}

                  {nutritionResult[recipe.id] && (
                    <div className="grid grid-cols-4 gap-1 text-center">
                      {[['Cal', nutritionResult[recipe.id].calories], ['Pro', nutritionResult[recipe.id].protein], ['Carb', nutritionResult[recipe.id].carbs], ['Fat', nutritionResult[recipe.id].fat]].map(([l, v]) => (
                        <div key={l as string} className="bg-slate-800 rounded-lg p-1.5">
                          <p className="text-[8px] text-slate-400 uppercase">{l}</p>
                          <p className="text-2xs font-black text-white">{v}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2 pt-1">
                    <button onClick={() => { setAddToPlanRecipe(recipe); setAddToPlanDay(weekDays[0].toISOString().slice(0, 10)); }}
                      className="flex-1 text-2xs font-black uppercase tracking-widest px-3 py-2 bg-brand hover:bg-brand-hover text-white rounded-lg transition-colors">
                      + Plan
                    </button>
                    <button onClick={() => openEditRecipe(recipe)}
                      className="text-2xs font-black uppercase tracking-widest px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors">
                      Edit
                    </button>
                    <button onClick={() => handleAnalyzeNutrition(recipe)} disabled={nutritionLoading === recipe.id}
                      className="text-2xs font-black uppercase tracking-widest px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors disabled:opacity-50">
                      {nutritionLoading === recipe.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add / Edit Recipe Modal */}
          {showAddRecipe && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center">
              <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-md" onClick={() => { setShowAddRecipe(false); resetRecipeForm(); }} />
              <div role="dialog" aria-modal="true" className="relative bg-slate-950 rounded-[2rem] p-8 w-full max-w-2xl shadow-2xl space-y-5 mx-4 max-h-[92vh] overflow-y-auto">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-black uppercase tracking-widest text-white">{editingRecipe ? 'Edit Recipe' : 'New Recipe'}</h3>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setRfStarred(p => !p)} title="Star recipe">
                      <Star className={`w-5 h-5 ${rfStarred ? 'text-amber-400 fill-amber-400' : 'text-slate-600 hover:text-amber-400'} transition-colors`} />
                    </button>
                    <button onClick={() => { setShowAddRecipe(false); resetRecipeForm(); }} aria-label="Close" className="p-2 hover:bg-slate-800 rounded-xl">
                      <X className="w-4 h-4 text-slate-400" />
                    </button>
                  </div>
                </div>

                {/* Name + basic fields */}
                <input value={rfName} onChange={e => setRfName(e.target.value)} placeholder="Recipe name *"
                  className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white text-sm focus:outline-none focus:border-blue-500" />

                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <label className="text-2xs font-black uppercase tracking-wider text-slate-500 mb-1 block">Servings</label>
                    <input value={rfServings} onChange={e => setRfServings(e.target.value)} type="number" min="1"
                      className="w-full px-3 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-white text-sm focus:outline-none focus:border-blue-500" />
                  </div>
                  <div>
                    <label className="text-2xs font-black uppercase tracking-wider text-slate-500 mb-1 block">Prep (min)</label>
                    <input value={rfPrep} onChange={e => setRfPrep(e.target.value)} type="number" min="0"
                      className="w-full px-3 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-white text-sm focus:outline-none focus:border-blue-500" />
                  </div>
                  <div>
                    <label className="text-2xs font-black uppercase tracking-wider text-slate-500 mb-1 block">Cook (min)</label>
                    <input value={rfCook} onChange={e => setRfCook(e.target.value)} type="number" min="0"
                      className="w-full px-3 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-white text-sm focus:outline-none focus:border-blue-500" />
                  </div>
                  <div>
                    <label className="text-2xs font-black uppercase tracking-wider text-slate-500 mb-1 block">Leftovers (days)</label>
                    <input value={rfLeftoverDays} onChange={e => setRfLeftoverDays(e.target.value)} type="number" min="0" max="7"
                      className="w-full px-3 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-white text-sm focus:outline-none focus:border-blue-500" />
                  </div>
                </div>

                {/* Ingredients */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-2xs font-black uppercase tracking-wider text-slate-400">Ingredients</p>
                    <button onClick={addIngredientRow} className="flex items-center gap-1 text-2xs text-blue-400 hover:text-blue-300 font-black uppercase tracking-wider">
                      <Plus className="w-3 h-3" /> Add
                    </button>
                  </div>
                  <div className="space-y-2">
                    {rfIngredients.map((ing, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <input value={ing.amount} onChange={e => updateIngredient(i, 'amount', e.target.value)} placeholder="Amt"
                          className="w-16 px-2 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-sm focus:outline-none focus:border-blue-500 text-center" />
                        <select value={ing.unit} onChange={e => updateIngredient(i, 'unit', e.target.value)}
                          className="w-24 px-2 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-sm focus:outline-none">
                          {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                        <input value={ing.item} onChange={e => updateIngredient(i, 'item', e.target.value)} placeholder="Ingredient *"
                          className="flex-1 px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-sm focus:outline-none focus:border-blue-500" />
                        <button onClick={() => removeIngredientRow(i)} className="p-1.5 text-slate-600 hover:text-red-400 transition-colors">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Instructions */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-2xs font-black uppercase tracking-wider text-slate-400">Instructions</p>
                    <button onClick={addStepRow} className="flex items-center gap-1 text-2xs text-blue-400 hover:text-blue-300 font-black uppercase tracking-wider">
                      <Plus className="w-3 h-3" /> Add Step
                    </button>
                  </div>
                  <div className="space-y-2">
                    {rfSteps.map((step, i) => (
                      <div key={i} className="flex gap-2 items-start">
                        <span className="w-6 h-8 flex items-center justify-center text-2xs font-black text-slate-500 shrink-0">{step.step}.</span>
                        <textarea value={step.text} onChange={e => updateStep(i, e.target.value)} rows={2} placeholder={`Step ${step.step}`}
                          className="flex-1 px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white text-sm focus:outline-none focus:border-blue-500 resize-none" />
                        <div className="flex flex-col gap-1 shrink-0">
                          <button onClick={() => moveStep(i, -1)} disabled={i === 0} className="p-1 text-slate-600 hover:text-white disabled:opacity-30 transition-colors">
                            <ChevronUp className="w-3 h-3" />
                          </button>
                          <button onClick={() => moveStep(i, 1)} disabled={i === rfSteps.length - 1} className="p-1 text-slate-600 hover:text-white disabled:opacity-30 transition-colors">
                            <ChevronDown className="w-3 h-3" />
                          </button>
                          <button onClick={() => removeStepRow(i)} className="p-1 text-slate-600 hover:text-red-400 transition-colors">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tags */}
                <div>
                  <p className="text-2xs font-black uppercase tracking-wider text-slate-500 mb-2">Dietary Tags</p>
                  <div className="flex gap-2 flex-wrap">
                    {RECIPE_TAGS.map(tag => (
                      <button key={tag} onClick={() => setRfTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag])}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${rfTags.includes(tag) ? 'bg-brand text-white' : 'bg-slate-800 text-slate-400 hover:text-white'}`}>
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Source URL */}
                <input value={rfSourceUrl} onChange={e => setRfSourceUrl(e.target.value)} placeholder="Source URL (optional)"
                  className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white text-sm focus:outline-none focus:border-blue-500" />

                <button onClick={handleSaveRecipe} disabled={!rfName.trim() || isSavingRecipe}
                  className="w-full py-3 bg-brand text-white font-black text-2xs uppercase tracking-widest rounded-full hover:bg-brand-hover disabled:opacity-50 transition-colors">
                  {isSavingRecipe ? 'Saving…' : editingRecipe ? 'Update Recipe' : 'Add Recipe'}
                </button>
              </div>
            </div>
          )}

          {/* Add to Plan modal */}
          {addToPlanRecipe && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center">
              <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-md" onClick={() => setAddToPlanRecipe(null)} />
              <div role="dialog" aria-modal="true" className="relative bg-slate-950 rounded-[2rem] p-8 w-full max-w-sm shadow-2xl space-y-4 mx-4">
                <h3 className="text-sm font-black uppercase tracking-widest text-white">Add to Meal Plan</h3>
                <p className="text-slate-400 text-sm">{addToPlanRecipe.name}</p>
                <div>
                  <label className="text-2xs font-black uppercase tracking-wider text-slate-500 mb-1 block">Day</label>
                  <select value={addToPlanDay} onChange={e => setAddToPlanDay(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white text-sm focus:outline-none">
                    {weekDays.map((d, i) => (
                      <option key={d.toISOString()} value={d.toISOString().slice(0, 10)}>{DAY_NAMES[i]} — {fmtDate(d)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-2xs font-black uppercase tracking-wider text-slate-500 mb-1 block">Meal</label>
                  <select value={addToPlanSlot} onChange={e => setAddToPlanSlot(e.target.value as MealSlot)}
                    className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white text-sm focus:outline-none">
                    {SLOTS.map(s => <option key={s} value={s}>{slotLabel(s)}</option>)}
                  </select>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setAddToPlanRecipe(null)}
                    className="flex-1 px-4 py-3 rounded-xl border border-slate-700 text-slate-400 text-xs font-black uppercase tracking-widest hover:bg-slate-800 transition-colors">
                    Cancel
                  </button>
                  <button disabled={!addToPlanDay} onClick={async () => {
                    if (!addToPlanDay || !addToPlanRecipe) return;
                    setIsAddingToPlan(true);
                    const existing = plan?.days?.[addToPlanDay]?.[addToPlanSlot];
                    const updated: PlannedMeal = {
                      ...existing,
                      items: [...(existing?.items || []), { recipeId: addToPlanRecipe.id, recipeName: addToPlanRecipe.name }],
                      plannedServings: existing?.plannedServings ?? addToPlanRecipe.servings,
                    };
                    await saveMeal(addToPlanDay, addToPlanSlot, updated);
                    setAddToPlanRecipe(null);
                    setIsAddingToPlan(false);
                  }}
                    className="flex-1 px-4 py-3 rounded-xl bg-brand text-white text-xs font-black uppercase tracking-widest hover:bg-brand-hover transition-colors disabled:opacity-50">
                    {isAddingToPlan ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Add'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════ PLANNER VIEW ══════════════════ */}
      {activeView === 'planner' && (
        <div>
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <button onClick={() => { const d = new Date(currentMonday); d.setDate(d.getDate() - 7); setCurrentMonday(d); }}
                className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white transition-colors">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h2 className="text-white text-xl font-bold">
                {t('meal.week_of', 'Week of')} {currentMonday.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </h2>
              <button onClick={() => { const d = new Date(currentMonday); d.setDate(d.getDate() + 7); setCurrentMonday(d); }}
                className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white transition-colors">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            <button onClick={() => setShowAiPanel(true)}
              className="flex items-center gap-2 px-4 py-2 bg-brand/20 border border-blue-500/40 text-blue-400 rounded-xl text-xs font-black uppercase tracking-wider hover:bg-brand/30 transition-all">
              <Sparkles className="w-3.5 h-3.5" />
              {t('meal.ai_meal_plan_btn', 'AI Plan')}
            </button>
          </div>

          {/* Week grid */}
          <div className="overflow-x-auto pb-2 -mx-6 px-6">
            <div className="grid grid-cols-7 gap-3 mb-6 min-w-[700px]">
              {weekDays.map((date, dayIndex) => {
                const dayStr = date.toISOString().slice(0, 10);
                const dayMeals = plan?.days?.[dayStr] || {};
                const isToday = new Date().toDateString() === date.toDateString();

                return (
                  <div key={dayStr} className={`rounded-xl border ${isToday ? 'border-blue-500/40 bg-blue-950/20' : 'border-slate-800 bg-slate-900'} p-3`}>
                    <div className="text-center mb-3">
                      <div className={`text-xs font-black uppercase tracking-widest ${isToday ? 'text-blue-400' : 'text-slate-400'}`}>{DAY_NAMES[dayIndex]}</div>
                      <div className={`text-sm font-semibold ${isToday ? 'text-blue-200' : 'text-white'}`}>{fmtDate(date)}</div>
                    </div>
                    <div className="space-y-2">
                      {SLOTS.map(slot => {
                        const meal = dayMeals[slot];
                        const guestType = meal ? getMealGuestType(meal) : null;
                        const guestColors = guestType ? GUEST_COLORS[guestType] : null;
                        const confirmedCount = meal?.rsvps ? Object.values(meal.rsvps).filter(v => v === 'confirmed').length : 0;
                        const hasNotes = !!meal?.notes?.trim();
                        const mealName = meal ? getMealDisplayName(meal) : '';

                        // Prep time indicator for dinner/lunch
                        const mealRecipes = meal?.items?.map(it => recipes.find(r => r.id === it.recipeId)).filter(Boolean) as Recipe[];
                        const totalCook = mealRecipes.reduce((sum, r) => sum + (r.cookTimeMins || 0), 0);
                        const totalPrep = mealRecipes.reduce((sum, r) => sum + (r.prepTimeMins || 0), 0);
                        const showPrepTime = (slot === 'dinner' || slot === 'lunch') && (totalCook + totalPrep) > 0;
                        const prepStart = showPrepTime ? calcPrepStartTime(totalCook, totalPrep, slot === 'dinner' ? 19 : 12) : '';

                        // Leftover hint
                        const isLeftoverDay = !!meal?.leftoversFrom;

                        return (
                          <div key={slot}>
                            <p className="text-[9px] font-black uppercase tracking-widest text-slate-600 mb-1">{slotLabel(slot)}</p>
                            <button
                              onClick={() => openSlot(dayStr, slot)}
                              className={`w-full text-left rounded-lg transition-colors ${
                                meal
                                  ? `bg-slate-800 hover:bg-slate-700 border ${guestColors ? guestColors.border : 'border-slate-700'} p-2.5`
                                  : 'border border-dashed border-slate-700 hover:border-slate-600 p-2.5 flex items-center justify-center text-slate-600 hover:text-slate-400'
                              }`}
                            >
                              {meal ? (
                                <div className="space-y-1.5">
                                  {isLeftoverDay && (
                                    <span className="text-[8px] font-black uppercase tracking-wider text-emerald-400 bg-emerald-900/20 px-1.5 py-0.5 rounded block w-fit">Leftovers</span>
                                  )}
                                  <p className="text-xs font-bold text-white leading-tight line-clamp-2">{mealName || 'Unnamed meal'}</p>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    {guestColors && guestType && (
                                      <span className={`text-[8px] font-black uppercase px-1.5 py-0.5 rounded ${guestColors.badge}`}>{guestType}</span>
                                    )}
                                    {(meal.plannedServings ?? 0) > 0 && (
                                      <span className="text-[8px] text-slate-500 flex items-center gap-0.5">
                                        <Users className="w-2.5 h-2.5" />{meal.plannedServings}
                                      </span>
                                    )}
                                    {confirmedCount > 0 && (
                                      <span className="text-[8px] text-emerald-400 flex items-center gap-0.5">
                                        <Check className="w-2.5 h-2.5" />{confirmedCount}
                                      </span>
                                    )}
                                    {hasNotes && <StickyNote className="w-2.5 h-2.5 text-slate-500" />}
                                  </div>
                                  {showPrepTime && (
                                    <p className="text-[8px] text-amber-400 flex items-center gap-0.5">
                                      <Clock className="w-2.5 h-2.5" />Start {prepStart}
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <Plus className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Grocery Sync */}
          <div className="mt-4 pt-6 border-t border-slate-800 flex items-center gap-4 flex-wrap">
            <button onClick={handlePreviewSync} disabled={isSyncing || !plan?.days}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand hover:bg-brand-hover text-white font-black text-xs uppercase tracking-widest disabled:opacity-50 transition-colors">
              {isSyncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />}
              Sync This Week's Groceries
            </button>
            {syncMessage && <p className="text-sm text-emerald-400 font-bold">{syncMessage}</p>}
          </div>

          {/* Sync preview modal */}
          {syncPreview && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center">
              <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-md" onClick={() => setSyncPreview(null)} />
              <div role="dialog" aria-modal="true" className="relative bg-slate-950 rounded-[2rem] p-8 w-full max-w-md shadow-2xl space-y-5 mx-4 max-h-[80vh] flex flex-col">
                <h3 className="text-sm font-black uppercase tracking-widest text-white">Grocery Sync Preview</h3>
                <div className="flex gap-4 text-xs">
                  <span className="text-red-400 font-bold">{syncPreview.removed} items to remove</span>
                  <span className="text-emerald-400 font-bold">{syncPreview.added} items to add</span>
                </div>
                {syncPreview.preview.length > 0 && (
                  <div className="flex-1 overflow-y-auto space-y-1.5 min-h-0">
                    <p className="text-2xs font-black uppercase tracking-wider text-slate-500 mb-2">Items to add:</p>
                    {syncPreview.preview.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-slate-900 rounded-lg">
                        <span className="text-emerald-400 text-xs">+</span>
                        <span className="text-sm text-white">{item.amount} {item.unit} {item.item}</span>
                      </div>
                    ))}
                  </div>
                )}
                {syncPreview.preview.length === 0 && (
                  <p className="text-slate-400 text-sm">No recipe-linked ingredients found. Add recipes to meal slots first.</p>
                )}
                <p className="text-2xs text-slate-500">Previously synced items for this week will be replaced. Manually added grocery items are not affected.</p>
                <div className="flex gap-3">
                  <button onClick={() => setSyncPreview(null)}
                    className="flex-1 px-4 py-3 rounded-xl border border-slate-700 text-slate-400 text-xs font-black uppercase tracking-widest hover:bg-slate-800 transition-colors">
                    Cancel
                  </button>
                  <button onClick={handleCommitSync} disabled={isSyncing}
                    className="flex-1 px-4 py-3 rounded-xl bg-brand text-white text-xs font-black uppercase tracking-widest hover:bg-brand-hover transition-colors disabled:opacity-50">
                    {isSyncing ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Confirm Sync'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* AI panel */}
          {showAiPanel && (
            <div className="fixed inset-0 z-[80] flex justify-end">
              <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowAiPanel(false)} />
              <aside className="relative w-96 bg-slate-950 border-l border-slate-800 shadow-2xl flex flex-col h-full animate-in slide-in-from-right-8 duration-300 overflow-hidden">
                <div className="p-5 border-b border-slate-800 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-4 h-4 text-blue-400" />
                    <h2 className="text-xs font-black uppercase tracking-widest text-white">AI Meal Planner</h2>
                  </div>
                  <button onClick={() => setShowAiPanel(false)} aria-label="Close" className="p-1.5 hover:bg-slate-800 rounded-lg transition-colors">
                    <X className="w-4 h-4 text-slate-400" />
                  </button>
                </div>
                <div className="p-5 border-b border-slate-800 space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {RECIPE_TAGS.map(pref => (
                      <button key={pref} onClick={() => setMealDietPrefs(prev => prev.includes(pref) ? prev.filter(p => p !== pref) : [...prev, pref])}
                        className={`text-2xs font-black uppercase tracking-wider px-3 py-1.5 rounded-xl border transition-all ${mealDietPrefs.includes(pref) ? 'bg-brand border-blue-500 text-white' : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'}`}>
                        {pref}
                      </button>
                    ))}
                  </div>
                  <button onClick={handleGenerateMealPlan} disabled={aiLoading}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-brand text-white rounded-xl font-black text-[11px] uppercase tracking-widest hover:bg-brand-hover transition-all disabled:opacity-60">
                    {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {aiLoading ? 'Generating…' : 'Generate 7-Day Plan'}
                  </button>
                  {aiError && <p className="text-2xs text-red-400 font-bold">{aiError}</p>}
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {aiMealPlan.length === 0 && !aiLoading && (
                    <div className="flex flex-col items-center justify-center h-32 text-slate-600 space-y-2">
                      <Sparkles className="w-8 h-8 opacity-30" />
                      <p className="text-2xs font-bold uppercase tracking-wider text-center">Your generated plan will appear here</p>
                    </div>
                  )}
                  {aiMealPlan.map((day, i) => (
                    <div key={i} className="bg-slate-900 border border-slate-800 rounded-xl p-3 space-y-2">
                      <p className="text-2xs font-black text-blue-400 uppercase tracking-wider">{day.date}</p>
                      {[['Breakfast', day.breakfast], ['Lunch', day.lunch], ['Dinner', day.dinner]].map(([label, meal]) => (
                        <div key={label as string} className="flex items-start gap-2">
                          <span className="text-[8px] font-black text-slate-500 uppercase w-14 shrink-0 pt-0.5">{label}</span>
                          <span className="text-2xs text-slate-300 font-medium leading-tight">{meal as string}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </aside>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════ MEAL SLOT EDIT MODAL ══════════════════ */}
      {editingSlot && editMeal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-md" onClick={() => { setEditingSlot(null); setEditMeal(null); }} />
          <div role="dialog" aria-modal="true" className="relative bg-slate-950 rounded-[2rem] p-8 w-full max-w-lg shadow-2xl space-y-5 mx-4 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-black uppercase tracking-widest text-white">
                  {DAY_NAMES[weekDays.findIndex(d => d.toISOString().slice(0, 10) === editingSlot.day)]} · {slotLabel(editingSlot.slot)}
                </h3>
                <p className="text-2xs text-slate-500 mt-0.5">{editingSlot.day}</p>
              </div>
              <div className="flex items-center gap-2">
                {plan?.days?.[editingSlot.day]?.[editingSlot.slot] && (
                  <button onClick={handleDeleteSlot} className="p-2 text-slate-600 hover:text-red-400 transition-colors" title="Remove meal">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <button onClick={() => { setEditingSlot(null); setEditMeal(null); }} className="p-2 hover:bg-slate-800 rounded-xl">
                  <X className="w-4 h-4 text-slate-400" />
                </button>
              </div>
            </div>

            {/* Recipe items */}
            <div>
              <p className="text-2xs font-black uppercase tracking-wider text-slate-400 mb-2">Meal Items</p>
              {(editMeal.items || []).length === 0 && <p className="text-2xs text-slate-600 mb-2">No items yet — pick a recipe below.</p>}
              <div className="space-y-1.5 mb-3">
                {(editMeal.items || []).map((item, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 bg-slate-800 rounded-xl">
                    <span className="text-sm text-white font-medium">{item.recipeName}</span>
                    <button onClick={() => removeItemFromMeal(i)} className="text-slate-600 hover:text-red-400 transition-colors ml-2">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              {/* Recipe picker */}
              <div className="border border-slate-800 rounded-xl overflow-hidden max-h-36 overflow-y-auto">
                {recipes.length === 0 ? (
                  <p className="text-2xs text-slate-500 p-3">No recipes — add some in the Recipes tab.</p>
                ) : (
                  recipes.map(r => {
                    const added = editMeal.items?.some(i => i.recipeId === r.id);
                    return (
                      <button key={r.id} onClick={() => addItemToMeal(r)} disabled={added}
                        className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center justify-between transition-colors ${added ? 'text-slate-600 bg-slate-900' : 'text-white hover:bg-slate-800'}`}>
                        <span>{r.name}</span>
                        {added ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Plus className="w-3.5 h-3.5 text-slate-500" />}
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {/* Planned servings */}
            <div>
              <label className="text-2xs font-black uppercase tracking-wider text-slate-400 mb-2 block flex items-center gap-1.5">
                <Users className="w-3 h-3" /> Planned Servings
              </label>
              <input type="number" min={1} max={50} value={editMeal.plannedServings ?? 2}
                onChange={e => setEditMeal(prev => ({ ...prev!, plannedServings: parseInt(e.target.value) || 1 }))}
                className="w-24 px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white text-sm focus:outline-none focus:border-blue-500 text-center" />
            </div>

            {/* Scaled ingredient preview */}
            {(editMeal.items?.length ?? 0) > 0 && (
              <div>
                <p className="text-2xs font-black uppercase tracking-wider text-slate-400 mb-2">Scaled Ingredients</p>
                <div className="space-y-1 bg-slate-900 rounded-xl p-3 max-h-32 overflow-y-auto">
                  {(editMeal.items || []).flatMap(item => {
                    const recipe = recipes.find(r => r.id === item.recipeId);
                    if (!recipe?.ingredients?.length) return [];
                    const scale = recipe.servings > 0 ? (editMeal.plannedServings ?? 2) / recipe.servings : 1;
                    return recipe.ingredients.map((ing, i) => {
                      const amt = (parseFloat(ing.amount) || 0) * scale;
                      return (
                        <p key={`${item.recipeId}-${i}`} className="text-2xs text-slate-400">
                          {amt % 1 === 0 ? amt : amt.toFixed(2)} {ing.unit} {ing.item}
                        </p>
                      );
                    });
                  })}
                </div>
              </div>
            )}

            {/* Household attendees */}
            {householdMembers.length > 0 && (
              <div>
                <p className="text-2xs font-black uppercase tracking-wider text-slate-400 mb-2">Household Attendees</p>
                <div className="flex flex-wrap gap-2">
                  {householdMembers.map(member => {
                    const isAttending = editMeal.guestIds?.includes(member.userId);
                    const rsvp = editMeal.rsvps?.[member.userId] ?? null;
                    return (
                      <button key={member.userId}
                        onClick={() => { toggleMealGuest(member.userId); toggleMealRsvp(member.userId); }}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-bold transition-all border ${
                          isAttending ? 'bg-brand/20 border-brand text-blue-300' : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
                        }`}>
                        <span>{member.name}</span>
                        {rsvp === 'confirmed' && <Check className="w-3 h-3 text-emerald-400" />}
                        {rsvp === 'abstained' && <X className="w-3 h-3 text-red-400" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* External guests */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-2xs font-black uppercase tracking-wider text-slate-400">External Guests</p>
                <button onClick={addExternalGuest} className="flex items-center gap-1 text-2xs text-blue-400 hover:text-blue-300 font-black uppercase tracking-wider">
                  <UserPlus className="w-3 h-3" /> Add
                </button>
              </div>
              <div className="space-y-2">
                {(editMeal.externalGuests || []).map((guest, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input value={guest.name} onChange={e => updateExternalGuest(i, 'name', e.target.value)} placeholder="Guest name"
                      className="flex-1 px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white text-sm focus:outline-none focus:border-blue-500" />
                    <select value={guest.type} onChange={e => updateExternalGuest(i, 'type', e.target.value)}
                      className="px-2 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white text-sm focus:outline-none">
                      <option value="family">Family</option>
                      <option value="friends">Friends</option>
                      <option value="colleagues">Colleagues</option>
                      <option value="other">Other</option>
                    </select>
                    <button onClick={() => removeExternalGuest(i)} className="p-1.5 text-slate-600 hover:text-red-400 transition-colors">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                {(editMeal.externalGuests || []).length === 0 && (
                  <p className="text-2xs text-slate-600">No external guests — family dinner only 🏠</p>
                )}
              </div>
            </div>

            {/* Dietary conflict check */}
            {(editMeal.items || []).length > 0 && getAttendeeDietary(editMeal).length > 0 && (
              <div className="space-y-1">
                {(editMeal.items || []).map(item => {
                  const recipe = recipes.find(r => r.id === item.recipeId);
                  if (!recipe) return null;
                  return <DietaryConflictBadge key={item.recipeId} recipe={recipe} attendeeDietary={getAttendeeDietary(editMeal)} />;
                })}
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="text-2xs font-black uppercase tracking-wider text-slate-400 mb-2 block flex items-center gap-1.5">
                <StickyNote className="w-3 h-3" /> Notes
              </label>
              <textarea value={editMeal.notes || ''} onChange={e => setEditMeal(prev => ({ ...prev!, notes: e.target.value }))}
                rows={2} placeholder="e.g. Sarah brings dessert, use the good plates…"
                className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white text-sm focus:outline-none focus:border-blue-500 resize-none" />
            </div>

            {/* Save */}
            <div className="flex gap-3 pt-1">
              <button onClick={() => { setEditingSlot(null); setEditMeal(null); }}
                className="flex-1 px-4 py-3 rounded-xl border border-slate-700 text-slate-400 text-xs font-black uppercase tracking-widest hover:bg-slate-800 transition-colors">
                Cancel
              </button>
              <button onClick={handleSaveSlot} disabled={isAddingToPlan}
                className="flex-1 px-4 py-3 rounded-xl bg-brand text-white text-xs font-black uppercase tracking-widest hover:bg-brand-hover transition-colors disabled:opacity-50">
                {isAddingToPlan ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Save Meal'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
